import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Mic, Square, Play, Pause, Check, AlertCircle, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { trackActivity } from "../components/ReclaimAssistant";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VoiceCloneSetupPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: intro, 2: record, 3: processing, 4: done
  const [isRecording, setIsRecording] = useState(false);
  const [recordingBlob, setRecordingBlob] = useState(null);
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [voiceName, setVoiceName] = useState("");
  const [error, setError] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  
  const getToken = () => localStorage.getItem("reclaim_token");

  // Sample text to read for voice clone
  const sampleText = `Welcome to Reclaim. I am here to guide you on a journey of transformation and inner peace. 
With each breath, I allow myself to relax deeper. My body knows how to heal, and I trust the process. 
I am safe, I am supported, and I am exactly where I need to be. 
Every day, in every way, I am becoming more aligned with my true self.`;

  const startRecording = async () => {
    try {
      setError(null);
      
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError("Your browser doesn't support audio recording.");
        return;
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      let mimeType = 'audio/webm';
      if (!MediaRecorder.isTypeSupported('audio/webm')) {
        if (MediaRecorder.isTypeSupported('audio/mp4')) {
          mimeType = 'audio/mp4';
        } else {
          mimeType = '';
        }
      }
      
      const options = mimeType ? { mimeType } : {};
      mediaRecorderRef.current = new MediaRecorder(stream, options);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const actualMimeType = mediaRecorderRef.current.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(audioChunksRef.current, { type: actualMimeType });
        setRecordingBlob(blob);
        setRecordingUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start(100);
      setIsRecording(true);
      toast.success("Recording started - read the text below");
    } catch (err) {
      let errMsg = "Could not access microphone.";
      if (err.name === 'NotAllowedError') {
        errMsg = "Microphone access was blocked. Please allow microphone access.";
      }
      setError(errMsg);
      toast.error(errMsg);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      toast.success("Recording saved!");
    }
  };

  const togglePlayback = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const clearRecording = () => {
    setRecordingBlob(null);
    setRecordingUrl(null);
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setIsPlaying(false);
  };

  const submitVoiceClone = async () => {
    if (!recordingBlob) {
      toast.error("Please record a voice sample first");
      return;
    }

    setIsSubmitting(true);
    setStep(3);

    try {
      const formData = new FormData();
      formData.append('audio_file', recordingBlob, 'voice_sample.webm');
      formData.append('voice_name', voiceName || 'My Voice');

      const response = await fetch(`${API_URL}/api/voice-studio/record-voice`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getToken()}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create voice clone');
      }

      const data = await response.json();
      toast.success("Voice clone created successfully!");
      setStep(4);
      
      // Track voice recording for smart suggestions
      trackActivity('voice_recorded', { voiceName });
    } catch (err) {
      console.error("Error creating voice clone:", err);
      setError(err.message || "Failed to create voice clone");
      toast.error(err.message || "Failed to create voice clone");
      setStep(2);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a1a2e] to-[#16213e] flex flex-col">
      {/* Header */}
      <div className="p-4 flex items-center gap-4">
        <button 
          onClick={() => navigate(-1)} 
          className="text-white/60 hover:text-white transition-colors"
        >
          <ArrowLeft size={24} />
        </button>
        <div>
          <h1 className="text-white font-semibold">Voice Clone Setup</h1>
          <p className="text-white/40 text-sm">Create your AI voice</p>
        </div>
      </div>

      <div className="flex-1 p-6">
        {/* Step 1: Introduction */}
        {step === 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="text-center">
              <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-amber-500/30 to-orange-500/30 flex items-center justify-center mb-4">
                <Sparkles size={36} className="text-amber-400" />
              </div>
              <h2 className="text-white text-2xl font-heading mb-2">Create Your Voice Clone</h2>
              <p className="text-white/60 text-sm">
                In just a few minutes, you'll have an AI version of your voice 
                that can read affirmations with your unique tone and energy.
              </p>
            </div>

            <div className="bg-white/5 rounded-2xl p-5 space-y-4">
              <h3 className="text-white font-medium">How it works:</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-amber-400 text-sm">1</span>
                  </div>
                  <p className="text-white/70 text-sm">Read a short sample text aloud (about 30 seconds)</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-amber-400 text-sm">2</span>
                  </div>
                  <p className="text-white/70 text-sm">Our AI learns your voice patterns and tone</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-amber-400 text-sm">3</span>
                  </div>
                  <p className="text-white/70 text-sm">Generate affirmations in your voice anytime!</p>
                </div>
              </div>
            </div>

            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
              <p className="text-amber-300/80 text-sm">
                <strong>Tip:</strong> Find a quiet space and speak naturally. 
                The more relaxed and authentic you sound, the better your clone will be.
              </p>
            </div>

            <button
              onClick={() => setStep(2)}
              className="w-full py-4 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold"
            >
              Let's Begin
            </button>
          </motion.div>
        )}

        {/* Step 2: Recording */}
        {step === 2 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="text-center">
              <h2 className="text-white text-xl font-heading mb-2">Record Your Voice</h2>
              <p className="text-white/60 text-sm">
                Read the text below in a calm, natural voice
              </p>
            </div>

            {/* Voice Name Input */}
            <div>
              <label className="block text-white/60 text-sm mb-2">Name your voice (optional)</label>
              <input
                type="text"
                value={voiceName}
                onChange={(e) => setVoiceName(e.target.value)}
                placeholder="e.g., My Voice, Healing Voice"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:border-amber-500/50 focus:outline-none"
              />
            </div>

            {/* Sample Text to Read */}
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
              <p className="text-xs text-amber-400/60 uppercase tracking-wider mb-2">Read this aloud:</p>
              <p className="text-white/90 text-sm leading-relaxed whitespace-pre-line">
                {sampleText}
              </p>
            </div>

            {/* Recording Button */}
            <div className="flex flex-col items-center">
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={isRecording ? stopRecording : startRecording}
                className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 shadow-lg ${
                  isRecording 
                    ? "bg-red-500 animate-pulse" 
                    : "bg-gradient-to-br from-amber-500 to-orange-500"
                }`}
              >
                {isRecording ? (
                  <Square className="w-8 h-8 text-white" />
                ) : (
                  <Mic className="w-10 h-10 text-white" />
                )}
              </motion.button>

              {isRecording ? (
                <p className="text-red-400 text-sm">Recording... Tap to stop</p>
              ) : (
                <p className="text-white/50 text-xs">Tap to start recording</p>
              )}
            </div>

            {error && (
              <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-4 flex items-start gap-3">
                <AlertCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-red-300 text-sm">{error}</p>
              </div>
            )}

            {/* Playback Preview */}
            {recordingUrl && !isRecording && (
              <div className="bg-white/5 rounded-xl p-4">
                <audio 
                  ref={audioRef} 
                  src={recordingUrl}
                  onEnded={() => setIsPlaying(false)}
                />
                <div className="flex items-center gap-4">
                  <button
                    onClick={togglePlayback}
                    className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center"
                  >
                    {isPlaying ? (
                      <Pause className="w-5 h-5 text-amber-400" />
                    ) : (
                      <Play className="w-5 h-5 ml-0.5 text-amber-400" />
                    )}
                  </button>
                  <div className="flex-1">
                    <p className="text-white text-sm">Your Recording</p>
                    <p className="text-white/50 text-xs">Tap to preview</p>
                  </div>
                  <button
                    onClick={clearRecording}
                    className="text-white/40 hover:text-white/70 text-xs"
                  >
                    Re-record
                  </button>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              onClick={submitVoiceClone}
              disabled={!recordingBlob || isSubmitting}
              className={`w-full py-4 rounded-2xl font-semibold flex items-center justify-center gap-2 ${
                recordingBlob && !isSubmitting
                  ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white"
                  : "bg-white/10 text-white/30 cursor-not-allowed"
              }`}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creating your voice...
                </>
              ) : (
                <>
                  <Sparkles size={18} />
                  Create My Voice Clone
                </>
              )}
            </button>
          </motion.div>
        )}

        {/* Step 3: Processing */}
        {step === 3 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex-1 flex flex-col items-center justify-center text-center"
          >
            <div className="w-24 h-24 rounded-full bg-amber-500/20 flex items-center justify-center mb-6">
              <Loader2 size={48} className="text-amber-400 animate-spin" />
            </div>
            <h2 className="text-white text-xl font-heading mb-2">Creating Your Voice Clone</h2>
            <p className="text-white/60 text-sm max-w-xs">
              Our AI is learning your voice patterns. This may take a moment...
            </p>
          </motion.div>
        )}

        {/* Step 4: Success */}
        {step === 4 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex-1 flex flex-col items-center justify-center text-center"
          >
            <div className="w-24 h-24 rounded-full bg-emerald-500/20 flex items-center justify-center mb-6">
              <Check size={48} className="text-emerald-400" />
            </div>
            <h2 className="text-white text-xl font-heading mb-2">Voice Clone Ready!</h2>
            <p className="text-white/60 text-sm max-w-xs mb-8">
              Your AI voice is now available. You can generate affirmations 
              in your own voice across Reclaim.
            </p>
            
            <div className="space-y-3 w-full max-w-xs">
              <button
                onClick={() => navigate("/private-suite")}
                className="w-full py-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold"
              >
                Go to Private Suite
              </button>
              <button
                onClick={() => navigate("/voice-studio")}
                className="w-full py-3 rounded-xl bg-white/10 text-white/70"
              >
                Back to Voice Studio
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default VoiceCloneSetupPage;
